import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import agentsService from '../../services/agentsService';
import SubscriptionManagement from './SubscriptionManagement';
import './AgentProfile.css';

const AgentProfile = () => {
  const { user, checkAuth } = useAuth();
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    businessName: '',
    termsConditions: '',
    backgroundTheme: 'none', // Default to no background
    logoUrl: '',
    commissionPercentage: 100
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);

  // Available gradient/texture themes
  const backgroundThemes = [
    { id: 'none', name: 'None (Clean)', preview: 'transparent' },
    { id: 'ocean-gradient', name: 'Ocean Breeze', preview: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
    { id: 'sunset-gradient', name: 'Sunset Glow', preview: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
    { id: 'forest-gradient', name: 'Forest Mist', preview: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
    { id: 'lavender-gradient', name: 'Lavender Dreams', preview: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)' },
    { id: 'golden-gradient', name: 'Golden Hour', preview: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' },
    { id: 'aurora-gradient', name: 'Aurora', preview: 'linear-gradient(135deg, #3f2b96 0%, #a8c0ff 100%)' },
    { id: 'geometric-pattern', name: 'Geometric', preview: 'url(data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%239C92AC" fill-opacity="0.1"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E)' },
    { id: 'dots-pattern', name: 'Dots', preview: 'url(data:image/svg+xml,%3Csvg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="%239C92AC" fill-opacity="0.15"%3E%3Ccircle cx="3" cy="3" r="3"/%3E%3Ccircle cx="13" cy="13" r="3"/%3E%3C/g%3E%3C/svg%3E)' },
    { id: 'waves-pattern', name: 'Waves', preview: 'url(data:image/svg+xml,%3Csvg width="100" height="20" viewBox="0 0 100 20" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M21.184 20c.357-.13.72-.264 1.088-.402l1.768-.661C33.64 15.347 39.647 14 50 14c10.271 0 15.362 1.222 24.629 4.928.955.383 1.869.74 2.75 1.072h6.225c-2.51-.73-5.139-1.691-8.233-2.928C65.888 13.278 60.562 12 50 12c-10.626 0-16.855 1.397-26.66 5.063l-1.767.662c-2.475.923-4.66 1.674-6.724 2.275h6.335zm0-20C13.258 2.892 8.077 4 0 4V2c5.744 0 9.951-.574 14.85-2h6.334zM77.38 0C85.239 2.966 90.502 4 100 4V2c-6.842 0-11.386-.542-16.396-2h-6.225zM0 14c8.44 0 13.718-1.21 22.272-4.402l1.768-.661C33.64 5.347 39.647 4 50 4c10.271 0 15.362 1.222 24.629 4.928C84.112 12.722 89.438 14 100 14v-2c-10.271 0-15.362-1.222-24.629-4.928C65.888 3.278 60.562 2 50 2 39.374 2 33.145 3.397 23.34 7.063l-1.767.662C13.223 10.84 8.163 12 0 12v2z" fill="%235C7CFA" fill-opacity="0.1" fill-rule="evenodd"/%3E%3C/svg%3E)' }
  ];

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const data = await agentsService.getProfile();
      setProfileData({
        firstName: data.firstName || data.first_name || '',
        lastName: data.lastName || data.last_name || '',
        email: data.email || '',
        phone: data.phone || '',
        businessName: data.businessName || data.business_name || '',
        termsConditions: data.termsConditions || data.terms_conditions || '',
        backgroundTheme: data.backgroundTheme || data.background_theme || 'none',
        logoUrl: data.logoUrl || data.logo_url || '',
        commissionPercentage: data.commissionPercentage || data.commission_percentage || 100
      });
    } catch (error) {
      console.error('Error loading profile:', error);
      setMessage('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  // Phone number validation function
  const validatePhoneNumber = (phone) => {
    // Allow numbers, spaces, hyphens, parentheses, and plus sign
    const phoneRegex = /^[\d\s\-\(\)\+]+$/;
    return phoneRegex.test(phone) && phone.replace(/[\s\-\(\)\+]/g, '').length >= 10;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleThemeSelect = (themeId) => {
    setProfileData(prev => ({
      ...prev,
      backgroundTheme: themeId
    }));
    
    // Apply theme immediately to preview
    applyBackgroundTheme(themeId);
  };

  const applyBackgroundTheme = (themeId) => {
    const theme = backgroundThemes.find(t => t.id === themeId);
    if (theme) {
      localStorage.setItem('agentBackgroundTheme', themeId);
      // Dispatch event to notify layout component
      window.dispatchEvent(new CustomEvent('backgroundThemeChanged', { detail: { theme: themeId } }));
    }
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;

    setLogoUploading(true);
    try {
      const formData = new FormData();
      formData.append('logo', file);

      const response = await fetch('/api/agents/logo', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setProfileData(prev => ({ ...prev, logoUrl: data.logoUrl }));
        setLogoFile(null);
        setLogoPreview(null);
        setMessage('Logo uploaded successfully!');
      } else {
        const errorData = await response.json();
        setMessage(errorData.error || 'Failed to upload logo');
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      setMessage('Failed to upload logo. Please try again.');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleLogoFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setMessage('Please select an image file');
        return;
      }
      
      // Validate file size (2MB limit)
      if (file.size > 2 * 1024 * 1024) {
        setMessage('Logo file must be less than 2MB');
        return;
      }

      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
      setMessage('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    // Validate phone number
    if (profileData.phone && !validatePhoneNumber(profileData.phone)) {
      setMessage('Phone number must contain only numbers, spaces, hyphens, parentheses, and plus signs');
      setSaving(false);
      return;
    }

    try {
      // Upload logo first if there's a new one
      if (logoFile) {
        await handleLogoUpload(logoFile);
      }

      await agentsService.updateProfile(profileData);
      setMessage('Profile updated successfully!');
      applyBackgroundTheme(profileData.backgroundTheme);
      
      // Refresh user context to update commission percentage in other components
      await checkAuth();
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto py-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="space-y-4">
              <div className="h-10 bg-gray-200 rounded"></div>
              <div className="h-10 bg-gray-200 rounded"></div>
              <div className="h-10 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-8">
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b">
          <h2 className="text-2xl font-bold text-gray-900">My Profile</h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Personal Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Personal Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={profileData.firstName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={profileData.lastName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={profileData.email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={profileData.phone}
                  onChange={handleInputChange}
                  onBlur={(e) => {
                    if (e.target.value && !validatePhoneNumber(e.target.value)) {
                      setMessage('Phone number must contain only numbers, spaces, hyphens, parentheses, and plus signs');
                    } else {
                      setMessage('');
                    }
                  }}
                  placeholder="(555) 123-4567"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Business Name (Optional)
                </label>
                <input
                  type="text"
                  name="businessName"
                  value={profileData.businessName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              {user?.role === 'independent_agent' && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Commission Percentage
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      name="commissionPercentage"
                      value={profileData.commissionPercentage}
                      onChange={handleInputChange}
                      min="0"
                      max="100"
                      step="0.01"
                      className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-gray-700">%</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    This percentage will be used to calculate your commission from the gross amount. Changes only affect new commissions.
                  </p>
                </div>
              )}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Terms and Conditions for Quotes
                </label>
                <textarea
                  name="termsConditions"
                  value={profileData.termsConditions}
                  onChange={handleInputChange}
                  rows={4}
                  placeholder="Enter your custom terms and conditions that will appear on all your quotes..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-sm text-gray-500 mt-1">
                  These terms will appear at the bottom of all your quotes. If left blank, a default message will be used.
                </p>
              </div>
            </div>
          </div>

          {/* Logo Upload Section */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Logo for Quotes</h3>
            <p className="text-sm text-gray-600 mb-4">
              Upload your logo to display on quotes sent to clients. Recommended: PNG or JPG, max 2MB.
            </p>
            
            {/* Current Logo Display */}
            {(logoPreview || profileData.logoUrl) && (
              <div className="mb-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                <p className="text-sm font-medium text-gray-700 mb-2">Current Logo:</p>
                <img 
                  src={logoPreview || profileData.logoUrl} 
                  alt="Current Logo" 
                  className="max-h-20 max-w-40 object-contain border border-gray-300 rounded"
                />
              </div>
            )}
            
            {/* Logo Upload Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload New Logo
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoFileChange}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              <p className="text-xs text-gray-500 mt-1">
                PNG, JPG, or GIF. Maximum file size: 2MB.
              </p>
            </div>
            
            {/* Upload Button */}
            {logoFile && (
              <button
                type="button"
                onClick={() => handleLogoUpload(logoFile)}
                disabled={logoUploading}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {logoUploading ? 'Uploading...' : 'Upload Logo'}
              </button>
            )}
          </div>

          {/* Background Theme Selection */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Desktop Background Theme</h3>
            <p className="text-sm text-gray-600 mb-4">
              Choose a background gradient or pattern that will appear on the sides of your screen on laptops and desktop computers.
              This won't affect mobile devices or smaller screens.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {backgroundThemes.map((theme) => (
                <button
                  key={theme.id}
                  type="button"
                  onClick={() => handleThemeSelect(theme.id)}
                  className={`relative p-4 rounded-lg border-2 transition-all ${
                    profileData.backgroundTheme === theme.id
                      ? 'border-blue-500 shadow-lg'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div
                    className="w-full h-20 rounded mb-2"
                    style={{
                      background: theme.preview,
                      backgroundSize: theme.id.includes('pattern') ? '20px 20px' : 'cover'
                    }}
                  />
                  <span className="text-sm font-medium text-gray-700">{theme.name}</span>
                  {profileData.backgroundTheme === theme.id && (
                    <div className="absolute top-2 right-2 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Success/Error Message */}
          {message && (
            <div className={`p-4 rounded-md ${
              message.includes('success') 
                ? 'bg-green-50 text-green-800' 
                : 'bg-red-50 text-red-800'
            }`}>
              {message}
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save Profile'}
            </button>
          </div>
        </form>
      </div>

      {/* Website Subscription Management - Temporarily hidden */}
      {/* <SubscriptionManagement /> */}
    </div>
  );
};

export default AgentProfile;
